import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './matchgame.css';
import trueSound from './assets/true.wav';
import wrongSound from './assets/wrong.wav';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

function MatchGame({ userData, onUserUpdate }) {
  const navigate = useNavigate();
  
  const imagePairs = Array.from({ length: 10 }, (_, index) => ({
    pairId: index + 1,
    image1: require(`./assets/hand-${index}.png`),
    image2: require(`./assets/number-${index}.png`)
  }));

  const createCards = () => {
    let gameCards = [];
    
    imagePairs.forEach(pair => {
      gameCards.push({
        id: `${pair.pairId}-1`,
        pairId: pair.pairId,
        cardType: 'image1',
        imageUrl: pair.image1,
        flipped: false,
        matched: false
      });
      
      gameCards.push({
        id: `${pair.pairId}-2`,
        pairId: pair.pairId,
        cardType: 'image2',
        imageUrl: pair.image2,
        flipped: false,
        matched: false
      });
    });
    
    return shuffleArray(gameCards);
  };

  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState(0);
  const [moves, setMoves] = useState(0);
  const [canFlip, setCanFlip] = useState(true);
  const [gameWon, setGameWon] = useState(false);

  const startGame = () => {
    const newCards = createCards();
    setCards(newCards);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setCanFlip(true);
    setGameWon(false);
  };

  useEffect(() => {
    startGame();

    Swal.fire({
      title: 'เกมจับคู่',
      html: `
        <p>จับคู่สัญลักษณ์ Finger กับตัวเลข</p>
        <p>เปิดแผ่นป้ายน้อยกว่า 20 ครั้ง รับ 50 คะแนน</p>
        <p>เปิดแผ่นป้าย 21 - 25 ครั้ง รับ 40 คะแนน</p>
        <p>เปิดแผ่นป้าย 26 - 30 ครั้ง รับ 30 คะแนน</p>
        <p>เปิดแผ่นป้ายมากกว่า 30 ครั้ง รับ 10 คะแนน</p>
      `,
      icon: 'info',
      confirmButtonText: 'เข้าใจแล้ว!',
      allowOutsideClick: false, // Prevent closing by clicking outside
      allowEscapeKey: false     // Prevent closing by pressing Esc key
    });

  }, []);

  useEffect(() => {
    if (flippedCards.length === 2) {
      setCanFlip(false);
      setMoves(moves + 1);
      
      const [card1, card2] = flippedCards;
      const isMatch = card1.pairId === card2.pairId;
      
      const sound = new Audio(isMatch ? trueSound : wrongSound);
      sound.play();

      setTimeout(() => {
        if (isMatch) {
          setCards(prevCards => 
            prevCards.map(card => 
              card.id === card1.id || card.id === card2.id 
                ? { ...card, matched: true } 
                : card
            )
          );
          setMatchedPairs(prev => prev + 1);
        } else {
          setCards(prevCards => 
            prevCards.map(card => 
              card.id === card1.id || card.id === card2.id 
                ? { ...card, flipped: false } 
                : card
            )
          );
        }
        
        setFlippedCards([]);
        setCanFlip(true);
      }, 1000);
    }
  }, [flippedCards]);

  useEffect(() => {
    if (matchedPairs === imagePairs.length) {
      setTimeout(() => {
        setGameWon(true);
        calculateAndSaveScore();
      }, 500);
    }
  }, [matchedPairs]);

  const calculateAndSaveScore = async () => {
    let scoreToAdd = 0;
    
    if (moves <= 20) {
      scoreToAdd = 50;
    } else if (moves <= 25) {
      scoreToAdd = 40;
    } else if (moves <= 30) {
      scoreToAdd = 30;
    } else {
      scoreToAdd = 10;
    }
    
    Swal.fire({
      title: 'เกมจบ!',
      html: `<p>คุณใช้การพลิก ${moves} ครั้ง</p>
             <p>คุณได้คะแนน ${scoreToAdd} คะแนน</p>`,
      icon: 'success',
      confirmButtonText: 'ตกลง',
      allowOutsideClick: false
    }).then(() => {
      navigate('/exercies');
    });
    
    if (userData?.username) {
      try {
        const userRef = doc(db, "assumption", userData.username);
        const docSnap = await getDoc(userRef);
        
        if (docSnap.exists()) {
          const currentData = docSnap.data();
          const newScore = (currentData.score || 0) + scoreToAdd;
          
          await updateDoc(userRef, {
            score: newScore,
            bonus: false
          });
          
          // แจ้ง App.js ให้รีเฟรชข้อมูลผู้ใช้
          if (onUserUpdate) {
            onUserUpdate(userData.username);
          }
        }
      } catch (error) {
        console.error("Error updating user data:", error);
      }
    }
  };

  const handleCardClick = (card) => {
    if (!canFlip || card.flipped || card.matched || flippedCards.length >= 2) {
      return;
    }
    
    setCards(prevCards => 
      prevCards.map(c => 
        c.id === card.id ? { ...c, flipped: true } : c
      )
    );
    
    setFlippedCards(prev => [...prev, card]);
  };

  const generateLetters = () => {
    return Array.from({ length: 20 }, (_, i) => 
      String.fromCharCode(65 + i)
    );
  };

  const getLetterForPosition = (index) => {
    const row = Math.floor(index / 5);
    const col = index % 5;
    return String.fromCharCode(65 + row * 5 + col);
  };

  const handleBackToExercies = () => {
    navigate('/exercies');
  };

  return (
    <div className="app">
      <div className="header">
        <h1>เกมจับคู่มือกับตัวเลข</h1>
        <p className="subtitle">จับคู่จำนวนนิ้วมือกับตัวเลขให้ถูกต้อง!</p>
      </div>
      
      <div className="main-container">
        <div className="game-board">
          {cards.map((card, index) => (
            <div 
              key={card.id}
              className={`card ${card.flipped || card.matched ? 'flipped' : ''} ${card.matched ? 'matched' : ''}`}
              onClick={() => handleCardClick(card)}
            >
              <div className="card-face card-front">
                {getLetterForPosition(index)}
              </div>
              <div 
                className="card-face card-back" 
                style={{ backgroundImage: `url(${card.imageUrl})` }}
              ></div>
            </div>
          ))}
        </div>
        
        <div className="game-info">
          {/* เพิ่มโลโก้ Wisdom ด้านบนของสถิติ */}
          <div className="game-logo">
            <img 
              src="/Wisdom-Logo.png" 
              alt="Wisdom Logo" 
              className="logo-image1"
            />

          </div>
          
          <div className="stats-container">
            <div className="stats">
              <div className="stat-box">
                <div className="stat-label">จำนวนครั้งที่พลิก</div>
                <div className="stat-value">{moves}</div>
              </div>
              
              <div className="stat-box">
                <div className="stat-label">คู่ที่ถูกต้อง</div>
                <div className="stat-value">{matchedPairs}/{imagePairs.length}</div>
              </div>
              
              <button className="back-button" onClick={handleBackToExercies}>
                กลับ
              </button>              
            </div>
          </div>
        </div>
      </div>
      
      {gameWon && (
        <div className="win-message show">
          <div className="win-content">
            <h2>ยินดีด้วย!</h2>
            <p>คุณชนะใน {moves} ครั้ง</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default MatchGame;